import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import InformativeTooltip from 'housecall-ui/dist/DesignSystem/DataDisplay/Tooltips/InformativeTooltip';
import Typography from 'housecall-ui/dist/DesignSystem/DataDisplay/Typography/Typography';
import FormControlLabel from 'housecall-ui/dist/DesignSystem/Inputs/FormControls/FormControlLabel';
import FormHelperText from 'housecall-ui/dist/DesignSystem/Inputs/FormControls/FormHelperText';
import Grid from 'housecall-ui/dist/DesignSystem/Layout/Grid/Grid';
import makeStyles from 'housecall-ui/dist/DesignSystem/Styles/makeStyles';
import useMediaQuery from 'housecall-ui/dist/DesignSystem/Styles/useMediaQuery';
import useTheme from 'housecall-ui/dist/DesignSystem/Styles/useTheme';
import InfoOutlined from 'housecall-ui/dist/IconsAndImages/Icons/InfoOutlined';
import WarningOutlined from 'housecall-ui/dist/IconsAndImages/Icons/WarningOutlined';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { change, FormErrors, getFormSyncErrors, getFormValues, hasSubmitFailed } from 'redux-form';
import { fetchAppStatus } from 'resources/Organization/Apps/AppsData';

import { WISETACK } from 'components/Apps/AppDetails/AppsData';
import { useDataFetcherV2 } from 'components/Common/Data/useDataFetcher';
import FormCheckbox from 'components/Common/Inputs/FormControls/FormCheckbox';
import { CUSTOMER_INFO_FORM } from 'components/Customers/AddOrEditCustomer/Redux/Actions';
import { PREQUAL_ON_CUSTOMER_CREATION } from 'components/LaunchDarkly/FeatureKeys';

interface FormErrorData {
  wisetack_prequal_application: boolean;
}

interface FormData {
  kind: 'business' | 'homeowner' | '';
  [key: string]: string;
}

const WISETACK_FIELD_NAME = 'wisetack_prequal_application';

const useStyles = makeStyles({
  tooltipTitle: {
    fontSize: '12px',
  },
});

const WisetackPrequalCheckbox = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down('md'));
  const maxWidth = isSmall ? 408 : 708;
  const [, wisetackStatus] = useDataFetcherV2(fetchAppStatus(WISETACK));
  const formErrors: FormErrors<FormErrorData> = useSelector(getFormSyncErrors(CUSTOMER_INFO_FORM));
  const { kind: customerKind } = (useSelector(getFormValues(CUSTOMER_INFO_FORM)) as FormData | undefined) || {};
  const failedSubmit = useSelector(hasSubmitFailed(CUSTOMER_INFO_FORM));
  const checkboxErrorMessage = formErrors?.wisetack_prequal_application;
  const showCheckboxError = failedSubmit && checkboxErrorMessage;
  const isWisetackEnabled = wisetackStatus.is_enabled;
  const isHomeownerCustomer = customerKind === 'homeowner';
  const { tooltipTitle } = useStyles();

  useEffect(() => {
    if (customerKind === 'business') {
      dispatch(change(CUSTOMER_INFO_FORM, WISETACK_FIELD_NAME, false));
    }
  }, [customerKind, dispatch]);

  if (!(useFlags()[PREQUAL_ON_CUSTOMER_CREATION] && isWisetackEnabled && isHomeownerCustomer)) {
    return null;
  }

  return (
    <Grid container spacing={1} alignItems="center" style={{ maxWidth }}>
      <Grid item xs="auto">
        <FormControlLabel
          style={{ marginRight: 0 }}
          control={<FormCheckbox name={WISETACK_FIELD_NAME} />}
          label="Send invitation to prequalify for financing"
        />
      </Grid>
      <Grid item xs="auto">
        <InformativeTooltip
          title={
            <Typography className={tooltipTitle}>
              Your customer will receive a short application to see the maximum amount they can borrow before a job is
              created.
            </Typography>
          }
        >
          <InfoOutlined color="secondary" />
        </InformativeTooltip>
      </Grid>
      {showCheckboxError && (
        <Grid item xs={12} md>
          <FormHelperText component="div" error>
            <Grid container spacing={1} justify="center">
              <Grid item>
                <WarningOutlined />
              </Grid>
              <Grid item>
                <Typography>{checkboxErrorMessage}</Typography>
              </Grid>
            </Grid>
          </FormHelperText>
        </Grid>
      )}
    </Grid>
  );
};

export default WisetackPrequalCheckbox;
